/**
 * OfferList Page
 *
 * Displays offers grouped by user with expandable rows.
 * Each user row can be expanded to show all offers for that user.
 */

import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Pagination } from '@/components/common/Table';
import { AssignmentStrategyBadge, RewardVariantBadge } from '@/components/common/Badge';
import { ActionMenu, type ActionMenuItem } from '@/components/common/ActionMenu';
import { OfferFilters } from '@/components/offers/OfferFilters';
import { useOfferData } from '@/app/hooks/useOfferData';
import { useHeaderActions } from '@/app/contexts/HeaderActionsContext';
import { offerService } from '@/app/services/offerService';
import { logger } from '@/app/utils/logger';
import { auditService } from '@/app/services';
import type { OfferResponse, OfferQueryParams } from '@/app/types/offers';
import './OfferList.scss';

// Sort options for user groups
type GroupSortKey = 'user_id' | 'count' | 'newest' | 'partner';
const GROUP_SORT_OPTIONS: { value: GroupSortKey; label: string }[] = [
  { value: 'newest', label: 'Most Recent' },
  { value: 'user_id', label: 'User ID' },
  { value: 'count', label: 'Most Offers' },
  { value: 'partner', label: 'Partner Name' },
];

// Grouped user data structure
interface UserOfferGroup {
  user_id: string;
  partner_name: string;
  partner_product_id: number | null;
  offers: OfferResponse[];
  totalOffers: number;
  earliestCreated: string;
  latestCreated: string;
}

export const OfferList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setActions } = useHeaderActions();
  const {
    offers,
    totalCount,
    currentPage,
    pageSize,
    totalPages,
    filters,
    isLoading,
    error,
    applyFilters,
    goToPage,
    changePageSize,
    refresh,
    resetFilters,
    pageSizeOptions,
  } = useOfferData();

  // Local filter state - updates on every keystroke without API calls
  const [localFilters, setLocalFilters] = useState<OfferQueryParams>(filters);

  // Sync local filters when Redux filters change (e.g., after reset)
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  // Handle filter changes (local state only, no API call)
  const handleFiltersChange = useCallback((newFilters: OfferQueryParams) => {
    setLocalFilters(newFilters);
  }, []);

  // Handle search - apply filters and trigger API call
  const handleSearch = useCallback(() => {
    applyFilters(localFilters);
  }, [applyFilters, localFilters]);

  // Log search audit after results return with accurate count
  const prevLoadingRef = React.useRef(isLoading);
  useEffect(() => {
    // Detect transition from loading to not loading (data just loaded)
    if (prevLoadingRef.current && !isLoading) {
      const filtersToLog: Record<string, unknown> = {};
      if (filters.user_id) filtersToLog.user_id = filters.user_id;
      if (filters.partner_product_id) filtersToLog.partner_product_id = filters.partner_product_id;
      if (filters.reward_id) filtersToLog.reward_id = filters.reward_id;
      if (filters.assignment_strategy)
        filtersToLog.assignment_strategy = filters.assignment_strategy;

      auditService
        .logRecordSearch('offer', filtersToLog, totalCount, '/offers')
        .catch((err) => logger.warn('Failed to log record search', { error: err }));
    }
    prevLoadingRef.current = isLoading;
  }, [isLoading, totalCount, filters]);

  // Handle clear - reset filters
  const handleClear = useCallback(() => {
    resetFilters();
  }, [resetFilters]);

  // Set header actions
  useEffect(() => {
    setActions(
      <>
        <button className="btn-secondary" onClick={refresh} disabled={isLoading}>
          ↻ Refresh
        </button>
        <button className="btn-create" onClick={() => navigate('/offers/new')}>
          + New Offer
        </button>
      </>
    );
    return () => setActions(null);
  }, [setActions, refresh, isLoading, navigate]);

  // Route change detection - refetch when returning to list page
  const prevPathnameRef = React.useRef<string>('');
  useEffect(() => {
    const isReturningToList =
      location.pathname === '/offers' &&
      prevPathnameRef.current !== '/offers' &&
      prevPathnameRef.current !== '';

    if (isReturningToList && !isLoading) {
      refresh();
    }

    prevPathnameRef.current = location.pathname;
  }, [location.pathname, refresh, isLoading]);

  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [groupSort, setGroupSort] = useState<GroupSortKey>('newest');

  // Group offers by user
  const groupedByUser = useMemo(() => {
    const groups = new Map<string, UserOfferGroup>();

    offers.forEach((offer) => {
      const existing = groups.get(offer.user_id);
      if (existing) {
        existing.offers.push(offer);
        existing.totalOffers++;
        if (offer.created_at < existing.earliestCreated) {
          existing.earliestCreated = offer.created_at;
        }
        if (offer.created_at > existing.latestCreated) {
          existing.latestCreated = offer.created_at;
        }
      } else {
        groups.set(offer.user_id, {
          user_id: offer.user_id,
          partner_name: offer.partner_name || 'Unknown',
          partner_product_id: offer.partner_product_id,
          offers: [offer],
          totalOffers: 1,
          earliestCreated: offer.created_at,
          latestCreated: offer.created_at,
        });
      }
    });

    // Sort offers within each group by priority
    groups.forEach((group) => {
      group.offers.sort((a, b) => a.display_priority - b.display_priority);
    });

    // Sort groups based on selected sort option
    const groupArray = Array.from(groups.values());
    switch (groupSort) {
      case 'user_id':
        return groupArray.sort((a, b) => a.user_id.localeCompare(b.user_id));
      case 'count':
        return groupArray.sort((a, b) => b.totalOffers - a.totalOffers);
      case 'partner':
        return groupArray.sort((a, b) => a.partner_name.localeCompare(b.partner_name));
      case 'newest':
      default:
        return groupArray.sort(
          (a, b) => new Date(b.latestCreated).getTime() - new Date(a.latestCreated).getTime()
        );
    }
  }, [offers, groupSort]);

  const toggleExpand = (userId: string) => {
    setExpandedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedUsers(new Set(groupedByUser.map((g) => g.user_id)));
  };

  const collapseAll = () => {
    setExpandedUsers(new Set());
  };

  const handleOfferClick = useCallback(
    (offer: OfferResponse) => {
      navigate(`/offers/${offer.offer_id}`);
    },
    [navigate]
  );

  const handleAddOfferForUser = (userId: string, partnerProductId: number | null) => {
    const params = new URLSearchParams();
    params.set('userId', userId);
    if (partnerProductId) {
      params.set('partnerProductId', partnerProductId.toString());
    }
    navigate(`/offers/new?${params.toString()}`);
  };

  // Handle offer deletion
  const handleDeleteOffer = useCallback(
    async (offer: OfferResponse) => {
      if (
        !window.confirm(
          `Are you sure you want to remove the offer "${offer.reward_name}" from user ${offer.user_id}? This action cannot be undone.`
        )
      ) {
        return;
      }

      try {
        const success = await offerService.deleteOffer(offer.offer_id);
        if (success) {
          // Refresh the list after deletion
          refresh();
        } else {
          alert('Failed to remove offer. Please try again.');
        }
      } catch (err) {
        logger.error('Failed to delete offer', { error: err, offerId: offer.offer_id });
        alert('Failed to remove offer. Please try again.');
      }
    },
    [refresh]
  );

  // Build action menu items for an offer row
  const getOfferActions = useCallback(
    (offer: OfferResponse): ActionMenuItem[] => [
      {
        label: 'View Details',
        icon: '👁',
        onClick: () => handleOfferClick(offer),
      },
      {
        label: 'Remove Offer',
        icon: '🗑️',
        variant: 'danger',
        onClick: () => handleDeleteOffer(offer),
      },
    ],
    [handleOfferClick, handleDeleteOffer]
  );

  if (isLoading) {
    return (
      <div className="offer-list-page">
        <div className="loading">Loading offers...</div>
      </div>
    );
  }

  return (
    <div className="offer-list-page">
      <OfferFilters
        filters={localFilters}
        onFiltersChange={handleFiltersChange}
        onSearch={handleSearch}
        onReset={handleClear}
      />

      {error && <div className="error-message">Error loading offers: {error}</div>}

      <div className="results-header">
        <div className="results-summary">
          {groupedByUser.length} users • {totalCount} total offers
        </div>
        <div className="results-controls">
          <div className="sort-control">
            <label htmlFor="group-sort">Sort by:</label>
            <select
              id="group-sort"
              value={groupSort}
              onChange={(e) => setGroupSort(e.target.value as GroupSortKey)}
            >
              {GROUP_SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="expand-controls">
            <button onClick={expandAll} className="btn-text">
              Expand All
            </button>
            <button onClick={collapseAll} className="btn-text">
              Collapse All
            </button>
          </div>
        </div>
      </div>

      <div className="grouped-offers">
        {groupedByUser.length === 0 ? (
          <div className="empty-state">No offers found matching your filters.</div>
        ) : (
          groupedByUser.map((group) => {
            const isExpanded = expandedUsers.has(group.user_id);
            return (
              <div key={group.user_id} className={`user-group ${isExpanded ? 'expanded' : ''}`}>
                {/* User Header Row */}
                <div className="user-header" onClick={() => toggleExpand(group.user_id)}>
                  <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>▶</span>
                  <a
                    href={`#/users/${group.user_id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate(`/users/${group.user_id}`);
                    }}
                    className="user-id-link"
                  >
                    {group.user_id}
                  </a>
                  <span className="offer-count">
                    {group.totalOffers} offer{group.totalOffers !== 1 ? 's' : ''}
                  </span>
                  <span className="partner-name">{group.partner_name}</span>
                  <span className="created-date">
                    Since {new Date(group.earliestCreated).toLocaleDateString()}
                  </span>
                  <button
                    className="btn-add-offer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddOfferForUser(group.user_id, group.partner_product_id);
                    }}
                  >
                    + Add Offer
                  </button>
                </div>

                {/* Expanded Offers */}
                {isExpanded && (
                  <div className="offers-detail">
                    {/* Desktop Table View */}
                    <table className="offers-table">
                      <thead>
                        <tr>
                          <th>Priority</th>
                          <th>Reward</th>
                          <th>Variant</th>
                          <th>Provider</th>
                          <th>Strategy</th>
                          <th>Created</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.offers.map((offer) => (
                          <tr
                            key={offer.offer_id}
                            onClick={() => handleOfferClick(offer)}
                            className="offer-row"
                          >
                            <td className="priority-cell">{offer.display_priority}</td>
                            <td className="reward-cell">{offer.reward_name}</td>
                            <td className="variant-cell">
                              {offer.reward_variant ? (
                                <RewardVariantBadge variant={offer.reward_variant} />
                              ) : (
                                '-'
                              )}
                            </td>
                            <td className="provider-cell">{offer.provider_name || '-'}</td>
                            <td className="strategy-cell">
                              <AssignmentStrategyBadge strategy={offer.assignment_strategy} />
                            </td>
                            <td className="date-cell">
                              {new Date(offer.created_at).toLocaleDateString()}
                            </td>
                            <td className="action-cell">
                              <ActionMenu
                                items={getOfferActions(offer)}
                                triggerLabel="Actions"
                                triggerIcon="▾"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Mobile List View - Clean rows */}
                    <div className="offers-cards">
                      {group.offers.map((offer) => (
                        <div
                          key={offer.offer_id}
                          className="offer-card"
                          onClick={() => handleOfferClick(offer)}
                        >
                          <div className="card-main">
                            <div className="card-title">{offer.reward_name}</div>
                            {offer.provider_name && (
                              <div className="card-provider">{offer.provider_name}</div>
                            )}
                            <div className="card-meta">
                              <span className="priority">#{offer.display_priority}</span>
                              <span>{new Date(offer.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <div className="card-badges">
                            {offer.reward_variant && (
                              <RewardVariantBadge variant={offer.reward_variant} />
                            )}
                            <AssignmentStrategyBadge strategy={offer.assignment_strategy} />
                          </div>
                          <div className="card-actions" onClick={(e) => e.stopPropagation()}>
                            <ActionMenu
                              items={getOfferActions(offer)}
                              triggerLabel=""
                              triggerIcon="▾"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalCount}
        itemsPerPage={pageSize}
        onPageChange={goToPage}
        pageSizeOptions={pageSizeOptions}
        onPageSizeChange={changePageSize}
        displayText={`Showing ${groupedByUser.length} of ${totalCount} user${totalCount !== 1 ? 's' : ''} (${offers.length} offer${offers.length !== 1 ? 's' : ''} on this page)`}
      />
    </div>
  );
};

export default OfferList;
